import React from 'react';
import PropTypes from 'prop-types';
import { Form, Button } from '@openedx/paragon';
import { Add as AddIcon } from '@openedx/paragon/icons';

/**
 * Generic reusable dynamic list of strings.
 * Renders a labelled text input per item with a Delete button, and an Add button.
 * Follows the same pattern as LearningOutcomesSection.
 *
 * Props:
 *   items      - string[] — the current list values
 *   fieldKey   - string  — passed back as the second arg to onChange
 *   label      - string  — item label prefix, e.g. "Disclaimer" → "Disclaimer 1"
 *   addLabel   - string  — text for the Add button
 *   deleteLabel - string — text for each Delete button
 *   placeholder - string — input placeholder text
 *   onChange   - (updatedArray: string[], fieldKey: string) => void
 */
const DynamicStringList = ({
  items,
  fieldKey,
  label,
  addLabel,
  deleteLabel,
  placeholder,
  onChange,
}) => {
  const handleInputChange = (value, index) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated, fieldKey);
  };

  const handleDelete = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    onChange(updated, fieldKey);
  };

  const handleAdd = () => {
    onChange([...items, ''], fieldKey);
  };

  return (
    <>
      <ul className="dynamic-string-list">
        {items.map((text, idx) => (
          <Form.Group
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            className="form-group-custom align-items-center d-flex"
          >
            <Form.Label isInline>
              {label} {idx + 1}
            </Form.Label>
            <Form.Control
              value={text}
              placeholder={placeholder}
              onChange={(e) => handleInputChange(e.target.value, idx)}
            />
            <Button
              variant="outline-primary"
              onClick={() => handleDelete(idx)}
            >
              {deleteLabel}
            </Button>
          </Form.Group>
        ))}
      </ul>
      <Button iconBefore={AddIcon} variant="primary" onClick={handleAdd}>
        {addLabel}
      </Button>
    </>
  );
};

DynamicStringList.defaultProps = {
  items: [],
  placeholder: '',
  deleteLabel: 'Delete',
};

DynamicStringList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
  fieldKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  addLabel: PropTypes.string.isRequired,
  deleteLabel: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default DynamicStringList;
